import {
  Fragment,
  useMemo,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { topVoiceToneName } from '@/music/voicing';
import { prettyChordSymbol } from '@/music/ireal/chordParser';
import { chartMarkKey, type ChartAnalysisMark } from './chartAnalysis';
import type { IRealChart, IRealMeasure } from '@/music/ireal/types';
import { computeLayout, structuralClasses, type Placement } from './chartLayout';
import {
  BarRepeatSign,
  ChordSymbol,
  EndingBracket,
  MeasureMarks,
  RepeatDots,
  TimeSig,
  ChartRoman,
} from './chartChrome';
import { useStore } from '@/state/store';
import { writtenMeasureId } from '@/audio/playCursor';
import { onControl, usePassageGesture } from './usePassageGesture';
import type { ReadingChartLayout } from './readingChartLayout';

interface ChartGridProps {
  readingLayout?: ReadingChartLayout;
  chart: IRealChart;
  /** Grid container class (`chart-view lead-sheet` or the diagram grid). */
  gridClassName: string;
  /** Per-measure base class (`chart-measure` or `optimized-measure`). */
  measureBaseClassName: string;
  selectedMeasureId?: string | null;
  /** Written-bar range highlight (practice loop / copy). */
  passageMeasureIds?: ReadonlySet<string>;
  /** Bars whose harmony the active reharm layer overrides (dashed marking). */
  reharmMeasureIds?: ReadonlySet<string>;
  insertionMeasureId?: string | null;
  playingMeasureId?: string | null;
  onSelectMeasure?: (id: string) => void;
  /** Drag across bars (a finger holds first), or shift-click, to select a written range. */
  onSelectPassage?: (fromId: string, toId: string, opts?: { extend?: boolean }) => void;
  /** Fired when a drag or hold committed a selection. */
  onPassageGesture?: () => void;
  /** A tap on a bar's paper (not on a control drawn in it): the start cursor. */
  onTapMeasure?: (id: string) => void;
  /** The written bar Play starts from, marked on the first bar drawn for it. */
  cursorMeasureId?: string | null;
  ariaLabel?: string;
  /** Cells per line (16 = iReal's 4 bars/line; 8 = 2 bars/line on phones). */
  cellsPerRow?: number;
  /** Grips that fit side by side in a bar's slot (computeLayout). */
  slotChords?: number;
  /** Page view: render only layout rows in [start, end), rebased to the grid's
   * top. Layout is computed over the WHOLE chart either way, so structure
   * (endings, row starts, simile runs) is identical on every page. */
  rowWindow?: readonly [number, number];
  /** Render the bar's content (chords or diagrams); not called for repeat bars. */
  renderBody: (m: IRealMeasure, place: Placement) => ReactNode;
}

/**
 * The shared 16-cell lead-sheet grid. It draws all the structural chrome —
 * barlines, section marks, endings, repeat signs, time signatures, simile bars
 * and staff text — so the chord score and the guitar-diagram view are laid out
 * identically. Only the per-bar body (chords vs. diagrams) differs.
 */
export function ChartGrid({
  chart,
  gridClassName,
  measureBaseClassName,
  selectedMeasureId,
  passageMeasureIds,
  reharmMeasureIds,
  insertionMeasureId,
  playingMeasureId,
  onSelectMeasure,
  onSelectPassage,
  onPassageGesture,
  onTapMeasure,
  cursorMeasureId,
  ariaLabel,
  cellsPerRow = 16,
  slotChords = 2,
  rowWindow,
  renderBody,
  readingLayout,
}: ChartGridProps) {
  const showBarNumbers = useStore((state) => state.showBarNumbers);
  const anacrusis = chart.measures[0]?.pickupBeats != null;
  const layout = useMemo(
    () => readingLayout?.placements ?? computeLayout(chart, cellsPerRow, slotChords),
    [chart, cellsPerRow, slotChords, readingLayout],
  );
  const alternateRows = new Set(
    chart.measures.flatMap((m, i) => (m.chords.some((c) => c.alternate) ? [layout[i].row] : [])),
  );
  // Rows that carry a volta: EVERY bar in such a row reserves top room for the
  // bracket (not just the ending's own bar), so the row's diagrams/chords stay
  // aligned instead of the ending bar dropping below its neighbours.
  const endingRows = useMemo(() => {
    const rows = new Set<number>();
    chart.measures.forEach((m, i) => {
      if (m.ending != null) rows.add(layout[i].row);
    });
    return rows;
  }, [chart, layout]);
  const measureIdAt = (target: EventTarget | null, clientX?: number, clientY?: number) => {
    const fromTarget = target instanceof Element ? target.closest('[data-measure-id]') : null;
    if (fromTarget) return fromTarget.getAttribute('data-measure-id');
    if (clientX == null || clientY == null) return null;
    const hit = document.elementFromPoint(clientX, clientY);
    return hit?.closest('[data-measure-id]')?.getAttribute('data-measure-id') ?? null;
  };
  const gesture = usePassageGesture({ onSelectPassage, onPassageGesture });
  const onGridClick = (e: MouseEvent<HTMLDivElement>) => {
    if (gesture.clickIsGesture()) return;
    if (!onTapMeasure || e.shiftKey || onControl(e.target, e.currentTarget)) return;
    const id = measureIdAt(e.target);
    if (id) onTapMeasure(id);
  };
  // The cursor marks the first bar drawn for its written bar (an unrolled
  // sheet draws a written bar once per pass, as `id#n`).
  const cursorIndex = cursorMeasureId
    ? chart.measures.findIndex((m) => writtenMeasureId(m.id) === cursorMeasureId)
    : -1;
  return (
    <div
      className={gridClassName}
      aria-label={ariaLabel}
      style={
        readingLayout
          ? ({
              '--reading-symbol-size': `${readingLayout.fontSize}px`,
              paddingLeft: 'var(--reading-gutter)',
              paddingRight: 'var(--reading-gutter)',
            } as CSSProperties)
          : cellsPerRow !== 16
            ? { gridTemplateColumns: `repeat(${cellsPerRow}, 1fr)` }
            : undefined
      }
      ref={onSelectPassage ? gesture.surfaceRef : undefined}
      onClick={onTapMeasure ? onGridClick : undefined}
      onPointerDown={onSelectPassage ? (e) => gesture.down(e, measureIdAt(e.target)) : undefined}
      onPointerMove={
        onSelectPassage
          ? (e) => gesture.move(e, () => measureIdAt(e.target, e.clientX, e.clientY))
          : undefined
      }
      onPointerUp={onSelectPassage ? gesture.up : undefined}
      onPointerCancel={onSelectPassage ? gesture.cancel : undefined}
    >
      {chart.measures.map((m, i) => {
        const place = layout[i];
        // Bar numbers count the WRITTEN bars; an anacrusis is unnumbered so
        // the first full bar reads 1 (the same rule as the score's).
        const barNumber = i + (anacrusis ? 0 : 1);
        const shownNumber = showBarNumbers && barNumber > 0 ? barNumber : null;
        // A section letter opening a row shares its corner with the bar
        // number, so the number rides after the letter (MeasureMarks).
        const numberAfterSection = !!m.section && !!place.rowStart;
        if (rowWindow && (place.row < rowWindow[0] || place.row >= rowWindow[1])) return null;
        const style: CSSProperties = {
          gridColumn: `${place.col + 1} / span ${place.span}`,
          gridRow: place.row + 1 - (rowWindow?.[0] ?? 0),
        };
        if (readingLayout) {
          style.gridColumn = '1 / -1';
          style.width = `${(place.span / 16) * 100}%`;
          style.marginLeft = `${(place.col / 16) * 100}%`;
          const symbolScale = readingLayout.placements[i].symbolScale;
          if (symbolScale && symbolScale < 1)
            Object.assign(style, { '--reading-bar-scale': symbolScale });
        }
        const playing = !!playingMeasureId && m.id === playingMeasureId;
        // The leading time signature sits in the left gutter, outside the first
        // bar (iReal style); a mid-tune change still renders inside its bar.
        const leadingTimeSig = i === 0 && m.timeSig;
        const cls = [
          measureBaseClassName,
          'barred',
          structuralClasses(m, place),
          // `has-ending` styles the volta bracket; `in-ending-row` reserves the
          // top room on every bar of that row so the bracket has space and the
          // row's content stays aligned. (The section letter sits in the gutter.)
          m.ending != null ? 'has-ending' : '',
          endingRows.has(place.row) ? 'in-ending-row' : '',
          readingLayout && alternateRows.has(place.row) ? 'reading-alternate-row' : '',
          m.id === selectedMeasureId ? 'measure-selected' : '',
          passageMeasureIds?.has(m.id) ? 'measure-in-passage' : '',
          reharmMeasureIds?.has(m.id) ? 'measure-reharm' : '',
          m.id === insertionMeasureId ? 'measure-insertion' : '',
          playing ? 'measure-playing' : '',
          i === cursorIndex ? 'measure-cursor' : '',
        ]
          .filter(Boolean)
          .join(' ');
        // When the bar is selectable, make it keyboard-operable like a button.
        const interactive = onSelectMeasure
          ? {
              role: 'button' as const,
              tabIndex: 0,
              'aria-label': `Bar ${i + 1}${
                m.chords.length ? `: ${m.chords.map((c) => c.symbol).join(', ')}` : ' (empty)'
              }`,
              onKeyDown: (e: KeyboardEvent) => {
                // Only when the bar itself is focused — Enter/Space on an inner
                // chord button must activate the button, not select the bar.
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectMeasure(m.id);
                }
              },
            }
          : {};
        return (
          <div
            key={m.id}
            className={cls}
            data-measure-id={m.id}
            data-reading-row={readingLayout ? place.row : undefined}
            style={style}
            aria-current={playing ? 'location' : undefined}
            onClick={onSelectMeasure ? () => onSelectMeasure(m.id) : undefined}
            {...interactive}
          >
            {shownNumber != null && !numberAfterSection && (
              <span className="measure-number" aria-hidden="true">
                {shownNumber}
              </span>
            )}
            {m.ending != null && <EndingBracket ending={m.ending} />}
            {leadingTimeSig && (
              <span className="time-sig-leading">
                <TimeSig sig={m.timeSig!} />
              </span>
            )}
            {m.open === 'repeat' && <RepeatDots side="open" />}
            {m.close === 'repeat' && <RepeatDots side="close" />}
            <MeasureMarks
              measure={m}
              sectionHead={numberAfterSection}
              barNumber={numberAfterSection ? shownNumber : null}
            />
            <div className="measure-cells">
              {m.timeSig && !leadingTimeSig && <TimeSig sig={m.timeSig} />}
              {m.barRepeat ? <BarRepeatSign n={m.barRepeat} /> : renderBody(m, place)}
            </div>
            {(m.staffText || m.directive) && (
              <div className={`measure-text${m.staffTextAbove && !m.directive ? ' above' : ''}`}>
                {m.directive ?? m.staffText}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface ChartViewProps {
  readingLayout?: ReadingChartLayout;
  chart: IRealChart;
  /** Grid density from useChartCells — zoomed-out charts reflow denser. */
  cellsPerRow?: number;
  /** Page view: only this layout-row range renders (see ChartGridProps). */
  rowWindow?: readonly [number, number];
  selectedChordId: string | null;
  selectedMeasureId: string | null;
  passageMeasureIds?: ReadonlySet<string>;
  reharmMeasureIds?: ReadonlySet<string>;
  /** The analysis lane's marks by measure id + chord index (chartAnalysis.ts). */
  analysisMarks?: ReadonlyMap<string, ChartAnalysisMark>;
  insertionMeasureId: string | null;
  playingMeasureId?: string | null;
  /** Absent in read mode: chords render as plain print, not buttons, so a
   * tap anywhere in a bar is a tap on the bar (the start cursor). */
  onSelectChord?: (id: string) => void;
  onSelectMeasure?: (id: string) => void;
  onSelectPassage?: (fromId: string, toId: string, opts?: { extend?: boolean }) => void;
  onPassageGesture?: () => void;
  onTapMeasure?: (id: string) => void;
  cursorMeasureId?: string | null;
}

/** A lead-sheet rendering of the chart on iReal Pro's 16-cell layout grid. */
export function ChartView({
  chart,
  cellsPerRow,
  rowWindow,
  selectedChordId,
  selectedMeasureId,
  passageMeasureIds,
  reharmMeasureIds,
  analysisMarks,
  insertionMeasureId,
  playingMeasureId,
  onSelectChord,
  onSelectMeasure,
  onSelectPassage,
  onPassageGesture,
  onTapMeasure,
  cursorMeasureId,
  readingLayout,
}: ChartViewProps) {
  return (
    <ChartGrid
      chart={chart}
      readingLayout={readingLayout}
      cellsPerRow={cellsPerRow}
      rowWindow={rowWindow}
      gridClassName={`chart-view lead-sheet${readingLayout ? ' reading-chart' : ''}`}
      measureBaseClassName="chart-measure"
      ariaLabel="Chord chart"
      selectedMeasureId={selectedMeasureId}
      passageMeasureIds={passageMeasureIds}
      reharmMeasureIds={reharmMeasureIds}
      insertionMeasureId={insertionMeasureId}
      playingMeasureId={playingMeasureId}
      onSelectMeasure={onSelectMeasure}
      onSelectPassage={onSelectPassage}
      onPassageGesture={onPassageGesture}
      onTapMeasure={onTapMeasure}
      cursorMeasureId={cursorMeasureId}
      renderBody={(m) =>
        m.pickupBeats != null && m.chords.every((c) => !c.root) ? (
          // A lead-in bar with no harmony used to print a full-width "N.C."
          // (or the empty "+"), indistinguishable from a real N.C. intro bar —
          // and then look like it was being skipped when repeats re-entered at
          // bar 1 (user report, Stella By Starlight). Name what it is.
          <span className="measure-pickup-label">pickup</span>
        ) : m.chords.length === 0 ? (
          <span className="measure-empty">{onSelectChord ? '+' : null}</span>
        ) : (
          m.chords.map((c, ci) => {
            const mark = analysisMarks?.get(chartMarkKey(m.id, ci));
            const body = (
              <>
                {c.alternate && (
                  <ChordSymbol
                    reading={!!readingLayout}
                    className="sc-alt"
                    symbol={prettyChordSymbol(c.alternate)}
                  />
                )}
                <ChordSymbol reading={!!readingLayout} className="sc-symbol" symbol={c.symbol} />
                {mark && <ChartRoman mark={mark} className="sc-roman" />}
                {c.targetTopNote && <span className="sc-lead">lead {c.targetTopNote}</span>}
                {Number.isInteger(c.preferredInversion) && (
                  <span className="sc-meta">
                    {c.chordType
                      ? `${topVoiceToneName(c.chordType, c.preferredInversion!)} on top requested`
                      : 'Voicing requested'}
                  </span>
                )}
              </>
            );
            const cls = `sequence-chord${c.id === selectedChordId ? ' selected' : ''}${c.small ? ' small' : ''}`;
            const grow = { flexGrow: Math.max(readingLayout ? 0.125 : 1, c.beats || 1) };
            // Reading is not editing: without a select handler the chord is
            // printed ink — a tap on it is a tap on its bar (the start
            // cursor), and hover/selection affordances belong to the button.
            return onSelectChord ? (
              <button
                type="button"
                key={c.id}
                className={cls}
                style={grow}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectChord(c.id);
                }}
              >
                {body}
              </button>
            ) : (
              <span key={c.id} className={cls} style={grow}>
                {body}
              </span>
            );
          })
        )
      }
    />
  );
}

/** Compact, non-interactive chart summary (e.g. for previews). */
export function ChartSummary({ chart }: { chart: IRealChart }) {
  return (
    <span className="chart-summary">
      {chart.measures.map((m, i) => (
        <Fragment key={m.id}>
          {i > 0 && ' | '}
          {m.barRepeat ? '%' : m.chords.map((c) => c.symbol).join(' ') || '—'}
        </Fragment>
      ))}
    </span>
  );
}
